import { useContext, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { ReactComponent as PLUSCIRCLE } from "../../../assets/PlusInCircleIcon.svg"
import { GeneralQueryKeys, Navigation } from "../../../api/constants"
import { P } from "../../../auth/permissions"
import { Authorized } from "../../../components/authentication/Authorized"
import Loading from "../../../components/common/Loading"
import { ModalContext } from "../../../components/contexts/ModalContext"
import BaseButtonRow from "../../../components/elements/BaseButtonRow"
import BaseGrowBody from "../../../components/elements/BaseGrowBody"
import BaseHeadBanner from "../../../components/elements/BaseHeadBanner"
import BaseInputSearch from "../../../components/elements/BaseInputSearch"
import BasePage from "../../../components/layout/BasePage"
import { useDelayWhenSearchWithPagination } from "../../../services/functions"
import { getEmployeesForTourAssignment, getTourList, getTourTasks } from "../api/apiCalls"
import { groupTasksByObject } from "../data/tourTasks"
import { useCreateTourMutation, useDeleteTourMutation } from "../api/services"
import AddTourModal from "../components/AddTourModal"
import TourCard from "../components/TourCard"
import TourTable from "../components/TourTable"
import TourViewSwitch from "../components/TourViewSwitch"

type TourListView = "table" | "cards"

const VIEW_STORAGE_KEY = "touren-list-view"

function readStoredView(): TourListView {
    try {
        return sessionStorage.getItem(VIEW_STORAGE_KEY) === "cards" ? "cards" : "table"
    } catch {
        return "table"
    }
}

export default function TourenPage() {
    const modalCon = useContext(ModalContext)
    const navigate = useNavigate()
    const queryClient = useQueryClient()
    const [searchTerm, setSearchTerm] = useState("")
    const [searchEmployee, setSearchEmployee] = useState("")
    const searchEmployeeParams = useDelayWhenSearchWithPagination(searchEmployee)
    const [view, setView] = useState<TourListView>(readStoredView)

    const switchView = (next: TourListView) => {
        setView(next)
        try {
            sessionStorage.setItem(VIEW_STORAGE_KEY, next)
        } catch {
            /* ignore */
        }
    }
    const { data, isLoading } = useQuery({
        queryKey: [GeneralQueryKeys.TOURS],
        queryFn: getTourList,
    })
    const { data: employees, isLoading: employeesLoading } = useQuery({
        queryKey: [GeneralQueryKeys.EMPLOYEE_NAMES_ONLY + "tour" + searchEmployeeParams],
        queryFn: () => getEmployeesForTourAssignment(searchEmployeeParams),
    })
    const { mutate: createTour } = useCreateTourMutation(queryClient)
    const { mutate: deleteTour } = useDeleteTourMutation(queryClient)
    const { data: weekTasks } = useQuery({
        queryKey: [GeneralQueryKeys.TOUR_TASKS + "currentWeek"],
        queryFn: () => getTourTasks({ currentWeekOnly: true }),
    })

    const tours = useMemo(() => {
        const query = searchTerm.trim().toLowerCase()
        const list = data ?? []
        if (!query) return list
        return list.filter((tour) => {
            const employee = tour.employee ? `${tour.employee.first_name} ${tour.employee.last_name}` : ""
            return tour.name.toLowerCase().includes(query) || employee.toLowerCase().includes(query)
        })
    }, [data, searchTerm])
    const tasksByObject = useMemo(() => groupTasksByObject(weekTasks ?? []), [weekTasks])

    return (
        <BasePage>
            <BaseHeadBanner
                title="Touren"
                button={
                    <Authorized allOf={[P.termine_management]}>
                        <PLUSCIRCLE className="w-8 h-8 cursor-pointer hover:text-primary" onClick={() => modalCon?.trigger(1)} />
                    </Authorized>
                }
            />
            <BaseButtonRow>
                <BaseInputSearch placeholder="Tour oder Mitarbeiter suchen..." searchTerm={searchTerm} setsearchTerm={setSearchTerm} classProps="!w-72 sm:w-auto" />
                <TourViewSwitch view={view} onChange={switchView} />
            </BaseButtonRow>
            <p className="text-xs text-gray-400 px-1 pb-2">Wöchentliche Fahrpläne (Mo-Fr).</p>
            {isLoading && <Loading />}
            {!isLoading && tours.length === 0 && (
                <BaseGrowBody classProps="overflow-x-hidden my-2 grow">
                    <p className="text-center text-gray-300 font-bold text-sm uppercase">Keine Touren</p>
                </BaseGrowBody>
            )}
            {!isLoading && tours.length > 0 && view === "table" && (
                <BaseGrowBody classProps="overflow-x-hidden my-2 grow">
                    <TourTable tours={tours} tasksByObject={tasksByObject} deleteTour={(id) => deleteTour(id)} />
                </BaseGrowBody>
            )}
            {!isLoading && tours.length > 0 && view === "cards" && (
                <BaseGrowBody classProps="pt-2 pb-8 overflow-y-scroll flex flex-col md:inline-grid md:grid-cols-2 lg:grid-cols-3 wxl:grid-cols-4 gap-4">
                    {tours.map((tour) => (
                        <TourCard
                            key={tour._id}
                            tour={tour}
                            tasksByObject={tasksByObject}
                            onOpen={() => navigate(Navigation.TOURS + tour._id)}
                            onDelete={(id) => deleteTour(id)}
                        />
                    ))}
                </BaseGrowBody>
            )}
            <AddTourModal
                modalId={1}
                employees={employees ?? []}
                employeesLoading={employeesLoading}
                onSearchEmployee={setSearchEmployee}
                onSave={({ name, employee }) => createTour(employee?._id ? { name, employee: employee._id } : { name })}
            />
        </BasePage>
    )
}
